import { useState } from 'react';
import type { BoardItem } from '@/lib/apiClient';
import { BOARD_LIMIT, boardMayBeCut, wantsLabel } from '@/lib/everyonesFeedback';
import { CATEGORY_LABEL, firstLine, fmtWhen, statusLabel } from '@/lib/feedbackMine';
import MeTooControl from './MeTooControl';

/**
 * Its own `fbe-` styles, declared here and not borrowed from the drawer's `fb-` ones or My feedback's `fbm-` ones: a class in this app
 * is live only while the component that declares it is mounted (there are no CSS Modules).
 */
const css = `
  .fbe-list { display: flex; flex-direction: column; gap: 8px; padding: 14px 18px 18px; }
  .fbe-lead { margin: 0; padding: 14px 18px 0; font: var(--ew-fw-regular) 12px/1.45 var(--ew-font); color: var(--ew-n-500); }
  .fbe-note { margin: 0; padding: 18px; font: var(--ew-fw-regular) 13px/1.45 var(--ew-font); color: var(--ew-n-500); }
  .fbe-err { margin: 0; padding: 18px; font: var(--ew-fw-regular) 13px/1.45 var(--ew-font); color: var(--ew-danger); }
  .fbe-item { border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); background: #fff; }
  .fbe-item.closed { background: var(--ew-n-50); }
  .fbe-row {
    display: flex; flex-direction: column; gap: 4px; align-items: stretch; width: 100%; text-align: left;
    padding: 10px 12px; background: transparent; border: 0; cursor: pointer; color: var(--ew-dark);
    font: var(--ew-fw-regular) 13px/1.35 var(--ew-font);
  }
  .fbe-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 8px; font: var(--ew-fw-medium) 11px/1.2 var(--ew-font); color: var(--ew-n-500); }
  .fbe-meta > span { white-space: nowrap; }
  .fbe-status { padding: 2px 8px; border-radius: var(--ew-r-pill); background: var(--ew-n-100); color: var(--ew-n-700); }
  .fbe-status.done { background: var(--ew-green-50, #E6F2EB); color: var(--ew-green); }
  .fbe-wants { color: var(--ew-dark); font-weight: var(--ew-fw-semibold); }
  .fbe-spacer { flex: 1; }
  .fbe-first { font-weight: var(--ew-fw-medium); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .fbe-detail { padding: 0 12px 12px; display: flex; flex-direction: column; gap: 10px; border-top: 1px solid var(--ew-n-100); }
  .fbe-msg { margin: 10px 0 0; white-space: pre-wrap; overflow-wrap: anywhere; font: var(--ew-fw-regular) 13px/1.45 var(--ew-font); color: var(--ew-dark); }
  .fbe-reply { padding: 8px 10px; border-radius: var(--ew-r-sm); background: var(--ew-n-50); border-left: 3px solid var(--ew-green); }
  .fbe-reply-head { font: var(--ew-fw-medium) 11.5px/1.2 var(--ew-font); color: var(--ew-n-500); margin-bottom: 4px; }
  .fbe-reply-head b { color: var(--ew-dark); }
  .fbe-reply p { margin: 0; white-space: pre-wrap; overflow-wrap: anywhere; font: var(--ew-fw-regular) 13px/1.45 var(--ew-font); color: var(--ew-dark); }
  .fbe-muted { margin: 0; font: var(--ew-fw-regular) 12px/1.4 var(--ew-font); color: var(--ew-n-500); }
`;

/** What the tab needs to say "me too" and undo it: the state of each attempt, and the two actions. */
export interface MeTooHandlers {
  /** Ids with a follow or undo on its way. */
  pending: ReadonlySet<number>;
  /** Why the last attempt on an id failed, in the API's words. */
  errors: ReadonlyMap<number, string>;
  /** Ids the person said "me too" on during this visit (so it can say "Added"). */
  justAdded: ReadonlySet<number>;
  onMeToo: (id: number) => void;
  onUndo: (id: number) => void;
}

interface Props {
  items: BoardItem[] | undefined;
  isLoading: boolean;
  /** The last request failed. Never shown as "nothing here": an outage is not an empty board. */
  isError: boolean;
  meToo: MeTooHandlers;
}

/**
 * What everyone has sent that is on the shared board, open requests first. Opening one shows the whole message and every reply, with
 * who wrote the replies, and, only there, the "me too" control: you read it before you agree with it.
 *
 * Nothing here says who SENT an item: the board does not carry it. (An item is marked "You sent this" for the person who did.)
 *
 * ⚠️ A FAILED LOAD IS NOT AN EMPTY BOARD. `data` is undefined when nothing has ever been loaded and the request failed, and `data ?? []`
 * would tell somebody that nobody has sent anything. And an empty board is the normal state at first, because everything starts out
 * unreviewed (what existed before the board, and all new feedback) and goes on the board only when an admin shows it: that is said as such,
 * not as a fault.
 *
 * ⚠️ A FAILED REFRESH IS NOT A FAILED LOAD. React Query keeps the last good answer when a later request fails, so `isError` can be true
 * with a list in hand (after "me too", which looks again). The list stays, with a note that it may be out of date: replacing it with an
 * error would hide what the person was reading, and the "Added" they were just told.
 */
export default function EveryonesFeedback({ items, isLoading, isError, meToo }: Props) {
  const [openId, setOpenId] = useState<number | null>(null);
  const styled = (body: React.ReactNode) => (<><style>{css}</style>{body}</>);

  if (isLoading) return styled(<p className="fbe-note">Loading…</p>);
  if (!items) {
    return styled(<p className="fbe-err" role="alert">We couldn’t load the board just now. Try again in a moment.</p>);
  }
  const stale = isError && <p className="fbe-lead" role="note">We couldn’t refresh the board just now, so this may be a little out of date.</p>;
  if (items.length === 0) {
    return styled(<>{stale}<p className="fbe-note">Nothing on the board yet. Feedback that other people send shows up here once an admin has read it and chosen to share it, so you can see what has already been asked and add your voice.</p></>);
  }

  return styled(
    <>
      {stale}
      <p className="fbe-lead">What other people have sent and an admin has chosen to share, without their names. If it is something you want too, open it and say “me too”: you’ll be told when it’s answered.</p>
      {/* ⚠️ The board sends no total, so exactly the limit may be a cut-off. Open requests come first, so what is missing is older closed ones. */}
      {boardMayBeCut(items.length) && (
        <p className="fbe-lead" role="note">The board shows at most {BOARD_LIMIT} items, open requests first, so some older closed ones may not be listed.</p>
      )}
      <div className="fbe-list">
        {items.map((i) => {
          const isOpen = i.id === openId;
          const label = statusLabel(i.status, i.closedReason);
          const wants = wantsLabel(i.followerCount);
          return (
            <div key={i.id} className={`fbe-item${i.status === 'closed' ? ' closed' : ''}`}>
              <button type="button" className="fbe-row" aria-expanded={isOpen} onClick={() => setOpenId(isOpen ? null : i.id)}>
                <span className="fbe-meta">
                  <span>{CATEGORY_LABEL[i.category] ?? i.category}</span>
                  <span className={`fbe-status${i.closedReason === 'completed' && i.status === 'closed' ? ' done' : ''}`}>{label}</span>
                  {wants && <span className="fbe-wants">{wants}</span>}
                  <span className="fbe-spacer" />
                  <span>{fmtWhen(i.createdAt)}</span>
                </span>
                <span className="fbe-first">{firstLine(i.message)}</span>
              </button>

              {isOpen && (
                <div className="fbe-detail">
                  <p className="fbe-msg">{i.message}</p>
                  {i.replies.map((r) => (
                    <div key={r.id} className="fbe-reply">
                      <div className="fbe-reply-head"><b>{r.authorName ?? 'The team'}</b> · {fmtWhen(r.createdAt)}</div>
                      <p>{r.body}</p>
                    </div>
                  ))}
                  {i.replies.length === 0 && i.status === 'open' && <p className="fbe-muted">No reply yet.</p>}
                  {i.status === 'closed' && (
                    <p className="fbe-muted">
                      {i.closedReason === 'completed' ? 'Marked done' : i.closedReason === 'not_planned' ? 'Closed as won’t do' : 'Closed'}
                      {i.closedAt ? ` · ${fmtWhen(i.closedAt)}` : ''}
                    </p>
                  )}
                  <MeTooControl
                    item={i}
                    about={firstLine(i.message, 50)}
                    justAdded={meToo.justAdded.has(i.id)}
                    pending={meToo.pending.has(i.id)}
                    error={meToo.errors.get(i.id) ?? null}
                    onMeToo={meToo.onMeToo}
                    onUndo={meToo.onUndo}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>,
  );
}
